import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import {
  ForgotPasswordFinishRequestParams,
  ForgotPasswordInitRequestParams,
  LoginParams,
  LoginResponse
} from '../models';
import { LocalStorageService, StorageItem } from 'src/app/shared/services/local-storage.service';

import { API_URL } from 'src/app/shared/constants/api-url.constants';
import { Account } from '../models/user.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ROUTER_PATHS } from 'src/app/shared/constants';
import { RefreshTokenService } from 'src/app/shared/services/refresh-token.service';
import { Router } from '@angular/router';
import { ServerMessage } from 'src/app/shared/models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isLoggedIn$ = new BehaviorSubject<boolean>(!!this.localStorageService.getItem(StorageItem.AuthToken));
  currentUser!: Account | null;
  currentUserSubject: BehaviorSubject<Account> = new BehaviorSubject(this.currentUser!);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly refreshTokenService: RefreshTokenService,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService
  ) {}

  login(loginParams: LoginParams): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(API_URL.account.login, loginParams).pipe(
      map((res) => {
        this.refreshTokenService.storeAuthTokens(res);
        this.refreshTokenService.startRefreshTokenTimer(res.access_token);
        this.isLoggedIn$.next(true);
        return res;
      })
    );
  }

  get isLoggedIn(): boolean {
    return this.isLoggedIn$.getValue();
  }

  onForgotPasswordInit(params: ForgotPasswordInitRequestParams): Observable<ServerMessage> {
    return this.httpClient.post<ServerMessage>(
      `${API_URL.account.root}/${API_URL.account.forgotPassword.root}/${API_URL.account.forgotPassword.init}`,
      params
    );
  }

  onForgotPasswordFinish(params: ForgotPasswordFinishRequestParams): Observable<void> {
    return this.httpClient.post<void>(
      `${API_URL.account.root}/${API_URL.account.forgotPassword.root}/${API_URL.account.forgotPassword.finish}`,
      params
    );
  }

  setCurrentUser$(value: Account) {
    this.currentUserSubject.next(value);
  }

  getCurrentUser$(): Observable<Account> {
    return this.currentUserSubject.asObservable();
  }

  getCurrentUser(fromDb = false): Observable<Account | null> {
    if (fromDb || !this.currentUser?.id) {
      const storedUser = this.localStorageService.getItem(StorageItem.CurrentUser);

      if (storedUser) {
        this.currentUser = storedUser as Account;
        this.currentUserSubject.next(storedUser as Account);
      }

      return this.httpClient.get<Account>(API_URL.account.root).pipe(
        map((res) => {
          this.currentUser = res;
          this.currentUserSubject.next(res);
          this.localStorageService.setItem(StorageItem.CurrentUser, res);
          return res;
        }),
        catchError((err) => {
          this.logOut();
          return of(null);
        })
      );
    }

    return of(this.currentUser);
  }

  logOut(): void {
    this.localStorageService.removeItem(StorageItem.AuthToken);
    this.localStorageService.removeItem(StorageItem.CurrentUser);
    this.isLoggedIn$.next(false);
    this.refreshTokenService.stopRefreshTokenTimer();
    this.currentUser = null;
    this.router.navigate([ROUTER_PATHS.auth.root]);
  }
}
